import React, { useState, useEffect } from 'react';
import './Feedback.css';

const Feedback = ({ currentUser }) => {
  const [feedback, setFeedback] = useState({
    name: currentUser ? currentUser.name : '',
    email: '',
    message: '',
    rating: 5
  });
  const [submitted, setSubmitted] = useState(false);
  const [feedbackResponses, setFeedbackResponses] = useState([]);

  // Load existing feedback responses from localStorage
  useEffect(() => {
    const savedFeedback = localStorage.getItem('bme-feedback-responses');
    if (savedFeedback) {
      setFeedbackResponses(JSON.parse(savedFeedback));
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFeedback(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Create feedback response object with timestamp
    const feedbackResponse = {
      ...feedback,
      timestamp: new Date().toISOString(),
      id: Date.now(),
      user: currentUser ? `${currentUser.name} (${currentUser.role})` : 'Anonymous'
    };
    
    // Save to localStorage
    const updatedResponses = [...feedbackResponses, feedbackResponse];
    setFeedbackResponses(updatedResponses);
    localStorage.setItem('bme-feedback-responses', JSON.stringify(updatedResponses));
    
    // Log to console
    console.log('Feedback submitted:', feedbackResponse);
    setSubmitted(true);
    
    // Reset form after submission
    setTimeout(() => {
      setFeedback({
        name: currentUser ? currentUser.name : '',
        email: '',
        message: '',
        rating: 5
      });
      setSubmitted(false);
    }, 3000);
  };

  // Download feedback responses as CSV
  const downloadFeedbackResponses = () => {
    if (feedbackResponses.length === 0) {
      alert('No feedback responses to download.');
      return;
    }
    
    // Create CSV content
    const headers = ['ID', 'Name', 'Email', 'Rating', 'Message', 'User', 'Timestamp'];
    const rows = feedbackResponses.map(response => [
      response.id,
      response.name,
      response.email,
      response.rating,
      `"${response.message.replace(/"/g, '""')}"`,
      response.user || 'Anonymous',
      response.timestamp
    ]);
    
    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\r\n');
    
    // Create download link
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `feedback_responses_${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="feedback-container">
      <div className="card">
        <h2 className="card-title">📝 Feedback & Suggestions</h2>
        <p>We value your feedback to improve our system. Please share your thoughts with us!</p>
        
        {submitted ? (
          <div className="success-message">
            <p>✅ Thank you for your feedback! We appreciate your input.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="name">Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={feedback.name}
                onChange={handleInputChange}
                placeholder="Your name"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={feedback.email}
                onChange={handleInputChange}
                placeholder="Your email"
                required
              />
            </div>
            
            <div className="form-group">
              <label>Rating</label>
              <div className="rating-container">
                {[1, 2, 3, 4, 5].map(star => (
                  <span 
                    key={star}
                    className={`star ${star <= feedback.rating ? 'filled' : ''}`}
                    onClick={() => setFeedback(prev => ({...prev, rating: star}))}
                  >
                    ★
                  </span>
                ))}
                <span className="rating-text">({feedback.rating} out of 5 stars)</span>
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="message">Message</label>
              <textarea
                id="message"
                name="message"
                rows="4"
                value={feedback.message}
                onChange={handleInputChange}
                placeholder="Please share your feedback, suggestions, or report any issues..."
                required
              ></textarea>
            </div>
            
            <button type="submit" className="btn btn-primary btn-block">
              Submit Feedback
            </button>
          </form>
        )}
        
        {/* Download button for feedback responses moved to the bottom */}
        {feedbackResponses.length > 0 && (
          <div className="download-feedback-bottom">
            <div className="feedback-summary">
              <h3>Feedback Responses Summary</h3>
              <p>Total responses: {feedbackResponses.length}</p>
            </div>
            <button 
              className="btn btn-info btn-block" 
              onClick={downloadFeedbackResponses}
            >
              📥 Download All Feedback Responses as CSV
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Feedback;